import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getStock, getStockHistory, getStockIndicators, addToPortfolio, addToWatchlist, getMarketStatus, getLimitOrders, getEntryOrders } from "@/lib/api";
import { StopDistance, orderLines, entryAsOrder } from "@/components/StopDistance";
import StockChart from "@/components/StockChart";
import AIPanel from "@/components/AIPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogClose
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, TrendingUp, TrendingDown, Star, Plus, Activity, BarChart2,
  ArrowUpRight, ArrowDownRight, Gauge
} from "lucide-react";
import { toast } from "sonner";

function StatItem({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary">{label}</p>
      <p className="font-mono text-sm font-medium text-bt-text-primary mt-0.5" data-testid={testId}>{value}</p>
    </div>
  );
}

function IndicatorGauge({ label, value, signal, signalColor }) {
  return (
    <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-2">{label}</p>
      <p className="font-mono text-2xl font-medium text-bt-text-primary">{value}</p>
      <Badge className={`mt-2 text-xs font-mono ${signalColor}`} data-testid={`indicator-signal-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {signal}
      </Badge>
    </div>
  );
}

export default function StockDetail() {
  const { symbol } = useParams();
  const [stock, setStock] = useState(null);
  const [history, setHistory] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [period, setPeriod] = useState("3m");
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState("");
  const [positionType, setPositionType] = useState("long");
  const [acquiredDate, setAcquiredDate] = useState("");
  const [mktStatus, setMktStatus] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const sym = symbol.toUpperCase();
    Promise.all([getLimitOrders().catch(() => ({ data: [] })), getEntryOrders().catch(() => ({ data: [] }))]).then(([l, e]) =>
      setOrders([...(l.data || []).filter(o => o.symbol === sym), ...(e.data || []).filter(o => o.symbol === sym).map(entryAsOrder)]));
  }, [symbol]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getStock(symbol).catch(() => null),
      getStockHistory(symbol, period).catch(() => ({ data: [] })),
      getStockIndicators(symbol).catch(() => null),
    ]).then(([s, h, ind]) => {
      setStock(s?.data || null);
      setHistory(h?.data || []);
      setIndicators(ind?.data || null);
      setLoading(false);
    });
  }, [symbol, period]);

  // Auto-refresh price every 60s
  useEffect(() => {
    getMarketStatus().then(r => setMktStatus(r.data)).catch(() => {});
    const interval = setInterval(() => {
      getStock(symbol).then(s => { if (s?.data) setStock(s.data); }).catch(() => {});
      getMarketStatus().then(r => setMktStatus(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [symbol]);

  const handleAddPortfolio = async () => {
    if (!stock || !shares || parseFloat(shares) <= 0) { toast.error("Enter valid shares"); return; }
    try {
      await addToPortfolio({
        symbol: stock.symbol,
        name: stock.name,
        shares: parseFloat(shares),
        buy_price: stock.price,
        asset_type: "stock",
        position_type: positionType,
        acquired_date: acquiredDate
      });
      toast.success(`Added ${positionType.toUpperCase()} ${shares} shares of ${stock.symbol}`);
      setShares("");
      setPositionType("long");
      setAcquiredDate("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const handleAddWatchlist = async () => {
    if (!stock) return;
    try {
      await addToWatchlist({ symbol: stock.symbol, name: stock.name, asset_type: "stock" });
      toast.success(`${stock.symbol} added to watchlist`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6">
        <Skeleton className="h-8 w-48 bg-bt-surface-2 mb-4" />
        <Skeleton className="h-64 w-full bg-bt-surface-2 mb-4" />
        <Skeleton className="h-40 w-full bg-bt-surface-2" />
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-20 text-center" data-testid="stock-not-found">
        <p className="text-xl font-heading text-bt-text-primary mb-4">Stock not found</p>
        <Link to="/" className="text-bt-gold hover:underline font-mono text-sm">Back to Dashboard</Link>
      </div>
    );
  }

  const hasLiveData = stock.price != null;
  const isPositive = (stock.change_pct || 0) >= 0;
  const periods = ["1m", "3m", "6m", "1y", "3y", "5y", "all"];

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" data-testid="stock-detail-page">
      {/* Breadcrumb */}
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-bt-text-secondary hover:text-bt-gold transition-colors mb-6 font-mono" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>

      {/* Stock Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary" data-testid="stock-symbol">
              {stock.symbol}
            </h1>
            <Badge variant="outline" className="border-bt-border-subtle text-bt-text-secondary font-mono text-xs">
              {stock.sector}
            </Badge>
            {mktStatus && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-mono ${
                mktStatus.us_market === "open" ? "bg-bt-positive/15 text-bt-positive border border-bt-positive/30" :
                mktStatus.us_market === "pre-market" || mktStatus.us_market === "after-hours" ? "bg-bt-gold/15 text-bt-gold border border-bt-gold/30" :
                "bg-bt-surface-2 text-bt-text-tertiary border border-bt-border-subtle"
              }`} data-testid="stock-market-status">
                <span className={`inline-flex rounded-full h-1.5 w-1.5 ${
                  mktStatus.us_market === "open" ? "bg-bt-positive" :
                  mktStatus.us_market === "pre-market" || mktStatus.us_market === "after-hours" ? "bg-bt-gold" : "bg-bt-text-tertiary"
                }`} />
                {mktStatus.us_market === "open" ? "MARKET OPEN" :
                 mktStatus.us_market === "pre-market" ? "PRE-MARKET" :
                 mktStatus.us_market === "after-hours" ? "AFTER-HOURS" : "MARKET CLOSED"}
              </span>
            )}
          </div>
          <p className="text-base text-bt-text-secondary font-body" data-testid="stock-name">{stock.name}</p>
        </div>
        <div className="flex items-end gap-6">
          <div>
            {hasLiveData ? (
              <>
                <p className="text-3xl lg:text-4xl font-mono font-medium tracking-tighter text-bt-text-primary" data-testid="stock-price">
                  ${stock.price.toLocaleString()}
                </p>
                <div className={`flex items-center gap-1 font-mono text-sm mt-1 ${isPositive ? "text-bt-positive" : "text-bt-negative"}`} data-testid="stock-change">
                  {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  ${Math.abs(stock.change || 0).toLocaleString()} ({isPositive ? "+" : ""}{stock.change_pct}%)
                </div>
              </>
            ) : (
              <p className="text-lg font-mono text-bt-text-tertiary" data-testid="stock-price">Awaiting live data</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleAddWatchlist}
              variant="outline"
              size="sm"
              className="border-bt-border-focus text-bt-text-primary hover:bg-bt-surface-2 font-body"
              data-testid="add-watchlist-btn"
            >
              <Star className="w-4 h-4 mr-1" /> Watch
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body" data-testid="add-portfolio-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add to My Picks
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary">
                <DialogHeader>
                  <DialogTitle className="font-heading text-lg">Add {stock.symbol} to Portfolio</DialogTitle>
                  <DialogDescription className="text-sm text-bt-text-secondary">Enter the number of shares to add at the current market price.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {/* Position Type Toggle */}
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">Position Type</Label>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => setPositionType("long")}
                        className={`flex-1 py-2 text-sm font-mono font-medium transition-colors ${
                          positionType === "long"
                            ? "bg-bt-positive/20 text-bt-positive border border-bt-positive/40"
                            : "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle hover:border-bt-border-focus"
                        }`}
                        data-testid="position-long-btn"
                      >
                        Long
                      </button>
                      <button
                        onClick={() => setPositionType("short")}
                        className={`flex-1 py-2 text-sm font-mono font-medium transition-colors ${
                          positionType === "short"
                            ? "bg-bt-negative/20 text-bt-negative border border-bt-negative/40"
                            : "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle hover:border-bt-border-focus"
                        }`}
                        data-testid="position-short-btn"
                      >
                        Short
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">{positionType === "short" ? "Short Price" : "Buy Price"}</Label>
                    <p className="font-mono text-lg text-bt-text-primary mt-1">${stock.price.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">Number of Shares</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={shares}
                      onChange={(e) => setShares(e.target.value)}
                      placeholder="e.g., 10"
                      className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                      data-testid="shares-input"
                    />
                  </div>
                  <div>
                    <Label className="text-bt-text-secondary font-mono text-xs">Acquisition Date</Label>
                    <Input
                      type="date"
                      value={acquiredDate}
                      onChange={(e) => setAcquiredDate(e.target.value)}
                      className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                      data-testid="acquired-date-input"
                    />
                  </div>
                  {shares && parseFloat(shares) > 0 && (
                    <p className="font-mono text-sm text-bt-text-secondary">
                      Total: ${(stock.price * parseFloat(shares)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  <DialogClose asChild>
                    <Button
                      onClick={handleAddPortfolio}
                      className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body"
                      data-testid="confirm-add-portfolio-btn"
                    >
                      Confirm
                    </Button>
                  </DialogClose>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart + Indicators */}
        <div className="lg:col-span-2 space-y-4">
          {/* Price Chart */}
          <div className="bg-bt-surface border border-bt-border-subtle p-4 md:p-6" data-testid="chart-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Price Chart <span className="text-[9px] text-bt-text-tertiary normal-case tracking-normal ml-1" data-testid="chart-source">({period === "all" ? "long-range" : period === "5y" ? "weekly" : "daily"} closes · Yahoo Finance)</span></h2>
              <div className="flex gap-1">
                {periods.map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1 text-xs font-mono transition-colors ${
                      period === p
                        ? "bg-bt-gold text-bt-bg"
                        : "text-bt-text-secondary hover:text-bt-text-primary hover:bg-bt-surface-2"
                    }`}
                    data-testid={`period-${p}`}
                  >
                    {p.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <StopDistance orders={orders} price={stock?.price} />
            <StockChart data={history} height={350} lines={orderLines(orders)} />
          </div>

          {/* Technical Indicators */}
          <div className="bg-bt-surface border border-bt-border-subtle p-4 md:p-6" data-testid="indicators-panel">
            <Tabs defaultValue="overview">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Technical Analysis</h2>
                <TabsList className="bg-bt-surface-2 h-7">
                  <TabsTrigger value="overview" className="text-xs px-3 h-6 data-[state=active]:bg-bt-gold/20 data-[state=active]:text-bt-gold" data-testid="tab-overview">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="momentum" className="text-xs px-3 h-6 data-[state=active]:bg-bt-gold/20 data-[state=active]:text-bt-gold" data-testid="tab-momentum">
                    Momentum
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="mt-0">
                {indicators ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <IndicatorGauge
                      label="RSI (14)"
                      value={indicators.rsi.value}
                      signal={indicators.rsi.signal}
                      signalColor={indicators.rsi.value < 30 ? "badge-buy" : indicators.rsi.value > 70 ? "badge-sell" : "badge-hold"}
                    />
                    <IndicatorGauge
                      label="MACD"
                      value={indicators.macd.macd}
                      signal={indicators.macd.signal_text}
                      signalColor={indicators.macd.histogram > 0 ? "badge-buy" : "badge-sell"}
                    />
                    <IndicatorGauge
                      label="SMA 20"
                      value={`$${indicators.moving_averages.sma_20?.toLocaleString() || "N/A"}`}
                      signal={stock.price > (indicators.moving_averages.sma_20 || 0) ? "Above" : "Below"}
                      signalColor={stock.price > (indicators.moving_averages.sma_20 || 0) ? "badge-buy" : "badge-sell"}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-bt-text-tertiary">Loading indicators...</p>
                )}
              </TabsContent>

              <TabsContent value="momentum" className="mt-0">
                {indicators ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-2">Bollinger Upper</p>
                        <p className="font-mono text-lg text-bt-text-primary">${indicators.bollinger_bands.upper}</p>
                      </div>
                      <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-2">Bollinger Lower</p>
                        <p className="font-mono text-lg text-bt-text-primary">${indicators.bollinger_bands.lower}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-2">MACD Signal</p>
                        <p className="font-mono text-lg text-bt-text-primary">{indicators.macd.signal}</p>
                      </div>
                      <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-2">MACD Histogram</p>
                        <p className={`font-mono text-lg ${indicators.macd.histogram > 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                          {indicators.macd.histogram}
                        </p>
                      </div>
                    </div>
                    {indicators.moving_averages.sma_50 && (
                      <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-2">SMA 50</p>
                        <p className="font-mono text-lg text-bt-text-primary">${indicators.moving_averages.sma_50.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-bt-text-tertiary">Loading...</p>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* Stock Stats */}
          <div className="bg-bt-surface border border-bt-border-subtle p-4 md:p-6" data-testid="stock-stats">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-4">Key Statistics</h2>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <StatItem label="Open" value={stock.open != null ? `$${stock.open}` : "—"} testId="stat-open" />
              <StatItem label="High" value={stock.high != null ? `$${stock.high}` : "—"} testId="stat-high" />
              <StatItem label="Low" value={stock.low != null ? `$${stock.low}` : "—"} testId="stat-low" />
              <StatItem label="Volume" value={stock.volume ? `${(stock.volume / 1e6).toFixed(1)}M` : "—"} testId="stat-volume" />
              <StatItem label="Market Cap" value={stock.market_cap || "—"} testId="stat-mcap" />
              <StatItem label="P/E Ratio" value={stock.pe_ratio != null ? stock.pe_ratio : "—"} testId="stat-pe" />
              <StatItem label="Div Yield" value={stock.dividend_yield != null ? `${stock.dividend_yield}%` : "—"} testId="stat-dy" />
              <StatItem label="52W High" value={stock.week52_high ? `$${stock.week52_high}` : "—"} testId="stat-52h" />
              <StatItem label="52W Low" value={stock.week52_low ? `$${stock.week52_low}` : "—"} testId="stat-52l" />
            </div>
          </div>

          {/* AI Analysis */}
          <AIPanel symbol={stock.symbol} stockName={stock.name} />
        </div>
      </div>
    </div>
  );
}
